/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useState } from 'react';
import has from 'lodash/has';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest, performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
import {
	RESOURCE_TYPE_ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES,
	ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES_STATUS_RESOURCE_KEY,
	ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES_VISIBILITY_API,
	USER_SEEN_CARDINALITY_UNKNOWN,
} from './resourceTypeIssueTransitionChangeboardingPreferences.constants.tsx';
import type {
	IssueTransitionChangeboardingPreferencesUpdateRequest,
	IssueTransitionChangeboardingPreferencesGetResponse,
	IssueTransitionChangeboardingPreferencesStatusResourceData,
	UseIssueTransitionChangeboardingPreferences,
	ResponseStatus,
} from './IssueTransitionChangeboardingPreferencesStatusResourceData.types.tsx';

export const issueTransitionChangeboardingPreferencesResource: RouteResource<IssueTransitionChangeboardingPreferencesStatusResourceData> =
	createResource<IssueTransitionChangeboardingPreferencesStatusResourceData>({
		type: RESOURCE_TYPE_ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES,
		getKey: () => ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES_STATUS_RESOURCE_KEY,
		getData: async (): Promise<IssueTransitionChangeboardingPreferencesStatusResourceData> => {
			let modalViewCount = 0;

			try {
				const apiResult = await performGetRequest(
					ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES_VISIBILITY_API,
				);
				if (fg('modern_issue_transition_enable_benefits_modal')) {
					if (!has(apiResult, 'value') || !has(apiResult, 'benefitsModalValue')) {
						const error = new Error('Invalid preference value');
						fireErrorAnalytics({
							meta: {
								id: 'issueTransitionChangeboardingPreferencesResource',
								packageName: 'jiraRouterResourcesIssueTransitionChangeboardingPreferences',
								teamName: 'gryffindor',
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
						});
					}
					return {
						numberOfTimesUserHasSeenChangeboarding: Number(apiResult.value) || 0,
						benefitsModal: {
							isDisabled: apiResult?.benefitsModal?.isDisabled || false,
							nextDateToShowBenefitsModal:
								apiResult?.benefitsModal?.nextDateToShowBenefitsModal || null,
						},
					};
				}

				if (typeof apiResult !== 'object' || !has(apiResult, 'value')) {
					const error = new Error('Invalid preference value');
					fireErrorAnalytics({
						meta: {
							id: 'issueTransitionChangeboardingPreferencesResource',
							packageName: 'jiraRouterResourcesIssueTransitionChangeboardingPreferences',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				} else {
					modalViewCount = Number(apiResult.value);
					return {
						numberOfTimesUserHasSeenChangeboarding: modalViewCount,
					};
				}
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode === 404) {
					if (fg('modern_issue_transition_enable_benefits_modal')) {
						return {
							numberOfTimesUserHasSeenChangeboarding: 0,
							benefitsModal: {
								isDisabled: false,
								nextDateToShowBenefitsModal: null,
							},
						};
					}
					return {
						numberOfTimesUserHasSeenChangeboarding: 0,
					};
				}
				fireErrorAnalytics({
					meta: {
						id: 'issueTransitionChangeboardingPreferencesResource',
						packageName: 'jiraRouterResourcesIssueTransitionChangeboardingPreferences',
						teamName: 'gryffindor',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
			return {
				numberOfTimesUserHasSeenChangeboarding: modalViewCount,
				benefitsModal: {
					isDisabled: true,
					nextDateToShowBenefitsModal: null,
				},
			};
		},
		isCritical: true,
	});

export const useIssueTransitionChangeboardingPreferences: UseIssueTransitionChangeboardingPreferences =
	() => {
		const {
			data,
			loading: loadingGetResource,
			error: errorGetResource,
			update,
		} = useResource(issueTransitionChangeboardingPreferencesResource);
		const [issueTransitionChangeboardingPreference, setIssueTransitionChangeboardingPreference] =
			useState<IssueTransitionChangeboardingPreferencesGetResponse>(() => {
				if (fg('modern_issue_transition_enable_benefits_modal')) {
					return {
						numberOfTimesUserHasSeenChangeboarding: Number(
							data?.numberOfTimesUserHasSeenChangeboarding,
						),
						benefitsModal: {
							isDisabled: Boolean(data?.benefitsModal?.isDisabled),
							nextDateToShowBenefitsModal:
								String(data?.benefitsModal?.nextDateToShowBenefitsModal) || null,
						},
					};
				}
				if (typeof data?.numberOfTimesUserHasSeenChangeboarding === 'number') {
					return data.numberOfTimesUserHasSeenChangeboarding;
				}
				return USER_SEEN_CARDINALITY_UNKNOWN;
			});

		const [responseStatusGet, setResponseStatusGet] = useState<ResponseStatus>('not started');
		const [responseStatusPut, setResponseStatusPut] = useState<ResponseStatus>('not started');
		const updateIssueTranstitionChangeboardingPreferences = useCallback(
			async (
				updates: IssueTransitionChangeboardingPreferencesUpdateRequest,
			): Promise<ResponseStatus> => {
				let response: ResponseStatus = 'loading';
				setResponseStatusPut('loading');
				const body =
					typeof updates !== 'number' && fg('modern_issue_transition_enable_benefits_modal')
						? {
								value: updates?.numberOfTimesUserHasSeenChangeboarding,
								benefitsModal: {
									isDisabled: updates?.benefitsModal?.isDisabled,
									nextDateToShowBenefitsModal: updates?.benefitsModal?.nextDateToShowBenefitsModal,
								},
							}
						: { value: updates };
				try {
					await performPutRequest(ISSUE_TRANSITION_CHANGEBOARDING_PREFERENCES_VISIBILITY_API, {
						body: JSON.stringify(body),
					});
					setResponseStatusPut('success');
					let updateData: IssueTransitionChangeboardingPreferencesStatusResourceData;
					if (typeof updates !== 'number' && fg('modern_issue_transition_enable_benefits_modal')) {
						updateData = {
							numberOfTimesUserHasSeenChangeboarding:
								updates?.numberOfTimesUserHasSeenChangeboarding,
							benefitsModal: {
								isDisabled: updates?.benefitsModal?.isDisabled || false,
								nextDateToShowBenefitsModal:
									String(updates?.benefitsModal?.nextDateToShowBenefitsModal) || null,
							},
						};
					}
					if (typeof updates === 'number') {
						updateData = {
							numberOfTimesUserHasSeenChangeboarding: updates,
						};
					}
					// oxlint-disable-next-line react/react-compiler
					update(() => updateData);
					response = 'success';
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						meta: {
							id: 'issueTransitionChangeboardingPreferencesUpdate',
							packageName: 'jiraRouterResourcesIssueTransitionChangeboardingPreferences',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					setResponseStatusPut('error');
					response = 'error';
				}
				return response;
			},
			[update],
		);

		useEffect(() => {
			if (loadingGetResource) {
				// oxlint-disable-next-line react/react-compiler
				setResponseStatusGet('loading');
				return;
			}

			if (errorGetResource) {
				setResponseStatusGet('error');
				return;
			}
			if (fg('modern_issue_transition_enable_benefits_modal')) {
				setResponseStatusGet('success');
				setIssueTransitionChangeboardingPreference({
					numberOfTimesUserHasSeenChangeboarding: Number(
						data?.numberOfTimesUserHasSeenChangeboarding,
					),
					benefitsModal: {
						isDisabled: data?.benefitsModal?.isDisabled || false,
						nextDateToShowBenefitsModal:
							String(data?.benefitsModal?.nextDateToShowBenefitsModal) || null,
					},
				});

				return;
			}

			if (typeof data?.numberOfTimesUserHasSeenChangeboarding === 'number') {
				setResponseStatusGet('success');
				setIssueTransitionChangeboardingPreference(data?.numberOfTimesUserHasSeenChangeboarding);
			}
		}, [
			loadingGetResource,
			errorGetResource,
			data?.numberOfTimesUserHasSeenChangeboarding,
			data?.benefitsModal?.isDisabled,
			data?.benefitsModal?.nextDateToShowBenefitsModal,
		]);

		return {
			stateGet: {
				responseStatus: responseStatusGet,
				state: issueTransitionChangeboardingPreference,
			},
			stateUpdate: {
				responseStatus: responseStatusPut,
				setState: updateIssueTranstitionChangeboardingPreferences,
			},
		};
	};
